/**
 * LEG-1 · Pantalla de textos legales (privacidad y términos).
 *
 * Los documentos se importan **del mismo Markdown que vive en `docs/`**
 * (`?raw` de Vite), no de una copia: un texto legal duplicado se desincroniza y
 * acaba mintiendo. `docs/` sigue siendo la fuente única; esta pantalla solo lo
 * renderiza dentro del juego, que es donde el jugador puede leerlo sin salir.
 */
import privacyMd from "../../docs/PRIVACIDAD.md?raw";
import termsMd from "../../docs/TERMINOS.md?raw";
import { injectThemeTokens } from "../theme";

export type LegalDoc = "privacy" | "terms";

const CSS = `
.lg-root{position:fixed;inset:0;z-index:26;display:none;align-items:center;justify-content:center;
  padding:var(--sp-4);font-family:var(--tb-font-ui);color:var(--tb-text);
  background:radial-gradient(130% 110% at 50% -10%,rgba(16,26,38,0.96),rgba(4,6,10,0.99));}
.lg-root.open{display:flex;}
.lg-card{position:relative;width:min(52rem,100%);height:min(48rem,100%);display:flex;flex-direction:column;
  background:linear-gradient(158deg,var(--tb-panel),var(--tb-abyss));box-shadow:var(--tb-lift);overflow:hidden;}
.lg-head{display:flex;align-items:center;gap:var(--sp-2);padding:var(--sp-3) var(--sp-4);
  border-bottom:1px solid var(--tb-line);}
.lg-tab{background:none;border:1px solid transparent;color:var(--tb-muted);font:inherit;
  font-size:var(--fs-xs);letter-spacing:0.16em;font-weight:800;padding:0.6em 1em;min-height:2.5rem;
  cursor:pointer;text-transform:uppercase;transition:color .12s ease,border-color .12s ease;}
.lg-tab.active{color:var(--tb-text);background:var(--tb-magenta-dim);border-color:rgba(255,46,99,0.45);}
@media (hover:hover) and (pointer:fine){.lg-tab:hover{color:var(--tb-text);}}
.lg-close{margin-left:auto;background:none;border:1px solid var(--tb-line);color:var(--tb-muted);
  font:inherit;font-size:var(--fs-sm);font-weight:800;width:2.25rem;height:2.25rem;cursor:pointer;
  clip-path:polygon(0.3rem 0,100% 0,100% calc(100% - 0.3rem),calc(100% - 0.3rem) 100%,0 100%,0 0.3rem);}
@media (hover:hover) and (pointer:fine){.lg-close:hover{color:var(--tb-magenta);border-color:var(--tb-magenta);}}
.lg-body{flex:1;overflow-y:auto;padding:var(--sp-5) var(--sp-5) var(--sp-6);
  font-size:var(--fs-sm);line-height:1.7;color:var(--tb-text-2);}
.lg-body h1{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;
  font-size:var(--fs-lg);letter-spacing:0.04em;margin:0 0 var(--sp-2);color:var(--tb-text);}
.lg-body h2{font-size:var(--fs-md);letter-spacing:0.06em;margin:var(--sp-5) 0 var(--sp-2);
  color:var(--tb-cyan);text-transform:uppercase;}
.lg-body h3{font-size:var(--fs-sm);letter-spacing:0.06em;margin:var(--sp-4) 0 var(--sp-2);color:var(--tb-text);}
.lg-body p,.lg-body ul,.lg-body ol{margin:0 0 var(--sp-3);}
.lg-body ul,.lg-body ol{padding-left:var(--sp-5);}
.lg-body li{margin:0 0 var(--sp-2);}
.lg-body strong{color:var(--tb-text);}
.lg-body code{background:var(--tb-panel-2);padding:0.15em 0.4em;font-family:var(--tb-font-mono);
  font-size:var(--fs-xs);}
.lg-body blockquote{margin:0 0 var(--sp-4);padding:var(--sp-3) var(--sp-4);
  border-left:0.2rem solid var(--tb-cyan);background:rgba(34,230,255,0.06);}
.lg-body table{width:100%;border-collapse:collapse;margin:0 0 var(--sp-4);font-size:var(--fs-xs);}
.lg-body th,.lg-body td{border:1px solid var(--tb-line);padding:0.6em 0.8em;text-align:left;}
.lg-body th{color:var(--tb-text);background:rgba(0,0,0,0.28);text-transform:uppercase;
  font-size:var(--fs-micro);letter-spacing:0.14em;}
.lg-body hr{border:0;border-top:1px solid var(--tb-line);margin:var(--sp-5) 0;}
@media (max-width:767.98px){.lg-root{padding:0;}.lg-card{width:100%;height:100%;}
  .lg-body{padding:var(--sp-4) var(--sp-4) var(--sp-5);}}
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}

/**
 * Markdown → HTML mínimo, suficiente para estos documentos (encabezados,
 * listas, tablas, citas, negrita, código y enlaces). Se escapa TODO antes de
 * aplicar formato, así el HTML final solo contiene las etiquetas que genera
 * esta función. Es un renderizador a medida a propósito: meter una dependencia
 * de Markdown para dos ficheros propios no compensa.
 */
function renderMarkdown(md: string): string {
  const inline = (s: string): string =>
    escapeHtml(s)
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      // Enlaces: solo el texto es visible; los relativos a docs/ no navegan.
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text: string, href: string) =>
        /^https?:\/\//.test(href)
          ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
          : `<strong>${text}</strong>`);

  const out: string[] = [];
  let list: "ul" | "ol" | null = null;
  let table: string[][] | null = null;

  /**
   * ¿Esta línea EMPIEZA un bloque nuevo? En Markdown un párrafo puede venir
   * partido en varias líneas de origen (los `.md` de `docs/` van ajustados a 80
   * columnas). Sin unirlas antes, cada línea saldría como su propio párrafo —y,
   * peor, una negrita que abarque dos líneas nunca cerraría.
   */
  const startsBlock = (line: string): boolean =>
    line === "" ||
    /^#{1,4}\s/.test(line) ||
    /^[-*]\s/.test(line) ||
    /^\d+\.\s/.test(line) ||
    line.startsWith("> ") ||
    /^\|.*\|$/.test(line) ||
    /^---+$/.test(line);

  // Une las líneas de continuación con la anterior antes de dar formato.
  const logical: string[] = [];
  for (const raw of md.split("\n")) {
    const line = raw.trimEnd();
    if (logical.length > 0 && !startsBlock(line) && logical[logical.length - 1] !== "") {
      logical[logical.length - 1] += " " + line.trim();
    } else {
      logical.push(line);
    }
  }

  const closeList = (): void => { if (list) { out.push(`</${list}>`); list = null; } };
  const closeTable = (): void => {
    if (!table) return;
    const [head, ...rows] = table;
    out.push("<table><thead><tr>" + head.map((h) => `<th>${inline(h)}</th>`).join("") + "</tr></thead><tbody>");
    for (const r of rows) out.push("<tr>" + r.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>");
    out.push("</tbody></table>");
    table = null;
  };

  for (const line of logical) {
    // Tabla: fila que empieza y acaba en "|". La fila de guiones se descarta.
    if (/^\|.*\|$/.test(line)) {
      closeList();
      const cells = line.slice(1, -1).split("|").map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue;
      (table ??= []).push(cells);
      continue;
    }
    closeTable();

    if (line === "") { closeList(); continue; }
    if (/^---+$/.test(line)) { closeList(); out.push("<hr>"); continue; }

    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { closeList(); out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`); continue; }

    if (line.startsWith("> ")) { closeList(); out.push(`<blockquote>${inline(line.slice(2))}</blockquote>`); continue; }

    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      if (list !== "ul") { closeList(); out.push("<ul>"); list = "ul"; }
      out.push(`<li>${inline(ul[1])}</li>`);
      continue;
    }
    const ol = /^\d+\.\s+(.*)$/.exec(line);
    if (ol) {
      if (list !== "ol") { closeList(); out.push("<ol>"); list = "ol"; }
      out.push(`<li>${inline(ol[1])}</li>`);
      continue;
    }

    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeList();
  closeTable();
  return out.join("");
}

const DOCS: Record<LegalDoc, { title: string; html: string }> = {
  privacy: { title: "PRIVACIDAD", html: renderMarkdown(privacyMd) },
  terms: { title: "TÉRMINOS", html: renderMarkdown(termsMd) },
};

export class LegalScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly tabs: Record<LegalDoc, HTMLButtonElement>;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    injectThemeTokens();
    if (!document.getElementById("lg-style")) {
      const s = document.createElement("style");
      s.id = "lg-style";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    this.root = document.createElement("div");
    this.root.className = "lg-root";

    const card = document.createElement("div");
    card.className = "lg-card tb-cut tb-edge";
    const head = document.createElement("div");
    head.className = "lg-head";

    const mkTab = (doc: LegalDoc): HTMLButtonElement => {
      const b = document.createElement("button");
      b.className = "lg-tab";
      b.textContent = DOCS[doc].title;
      b.onclick = () => this.select(doc);
      head.appendChild(b);
      return b;
    };
    this.tabs = { privacy: mkTab("privacy"), terms: mkTab("terms") };

    const close = document.createElement("button");
    close.className = "lg-close";
    close.textContent = "✕";
    close.title = "Cerrar (Esc)";
    close.onclick = () => this.close();
    head.appendChild(close);

    this.body = document.createElement("div");
    this.body.className = "lg-body tb-scroll";

    card.appendChild(head);
    card.appendChild(this.body);
    this.root.appendChild(card);
    // Clic fuera de la tarjeta = cerrar.
    this.root.onclick = (e) => { if (e.target === this.root) this.close(); };
    document.body.appendChild(this.root);
  }

  private select(doc: LegalDoc): void {
    for (const key of Object.keys(this.tabs) as LegalDoc[]) {
      this.tabs[key].classList.toggle("active", key === doc);
    }
    this.body.innerHTML = DOCS[doc].html;
    this.body.scrollTop = 0;
  }

  /** ¿Está tapando la pantalla? (ver `covered` en main.ts) */
  get isOpen(): boolean {
    return this.root.classList.contains("open");
  }

  open(doc: LegalDoc = "privacy"): void {
    this.select(doc);
    this.root.classList.add("open");
    // Esc cierra. El handler se registra en captura para ganarle al resto de la
    // UI (menú de pausa, chat) mientras el modal legal está encima de todo.
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    };
    window.addEventListener("keydown", this.escHandler, true);
  }

  close(): void {
    this.root.classList.remove("open");
    if (this.escHandler) {
      window.removeEventListener("keydown", this.escHandler, true);
      this.escHandler = null;
    }
  }
}
